import { apiClient } from '../utils/apiClient';
import type {
    Team,
    TeamMembership,
    TeamInvite,
    CreateTeamRequest,
    UpdateTeamRequest,
    TeamMemberRole,
    UserTeamInfo,
    TeamKeyMode,
    UpdateTeamKeyModeRequest,
    UpdateTeamKeyModeResponse,
} from '@arrowise/shared';

/**
 * Team with memberships for detailed views
 */
export interface TeamWithMemberships extends Team {
    memberships?: TeamMembership[];
    memberCount?: number;
}

/**
 * Teams response from API
 */
export interface TeamsResponse {
    teams: UserTeamInfo[];
    pendingInvites: TeamInvite[];
}

/**
 * Team details response from API
 */
export interface TeamDetailsResponse {
    team: Team;
    members: TeamMembership[];
    userMembership: TeamMembership;
}

/**
 * Team response from API
 */
export interface TeamResponse {
    team: Team;
}

/**
 * Team invite response from API
 */
export interface TeamInviteResponse {
    invite: TeamInvite;
}

/**
 * Check slug availability response
 */
export interface SlugAvailabilityResponse {
    available: boolean;
    slug: string;
}

/**
 * Generate slug response
 */
export interface GenerateSlugResponse {
    slug: string;
    available: boolean;
}

/**
 * Team Service Class
 */
class TeamService {
    /**
     * Get user's teams and pending invites
     */
    async getUserTeams(): Promise<{ teams: UserTeamInfo[]; pendingInvites: TeamInvite[] }> {
        const response = await apiClient.get<TeamsResponse>('/teams');
        if (!response.data) {
            throw new Error('Failed to fetch user teams');
        }
        return {
            teams: response.data.teams,
            pendingInvites: response.data.pendingInvites
        };
    }

    /**
     * Get team details by ID (includes members)
     */
    async getTeamDetailsById(teamId: string): Promise<TeamDetailsResponse> {
        const response = await apiClient.get<TeamDetailsResponse>(`/teams/${teamId}`);
        if (!response.data) {
            throw new Error('Failed to fetch team details');
        }
        return response.data;
    }

    /**
     * Get team by ID (basic info only)
     */
    async getTeamById(teamId: string): Promise<Team> {
        const response = await apiClient.get<TeamResponse>(`/teams/${teamId}`);
        if (!response.data) {
            throw new Error('Failed to fetch team');
        }
        return response.data.team;
    }

    /**
     * Create a new team
     */
    async createTeam(teamData: CreateTeamRequest): Promise<Team> {
        const response = await apiClient.post<TeamResponse>('/teams', teamData);
        if (!response.data) {
            throw new Error('Failed to create team');
        }
        return response.data.team;
    }

    /**
     * Update team information
     */
    async updateTeam(teamId: string, updateData: UpdateTeamRequest): Promise<Team> {
        const response = await apiClient.put<TeamResponse>(`/teams/${teamId}`, updateData);
        if (!response.data) {
            throw new Error('Failed to update team');
        }
        return response.data.team;
    }

    /**
     * Invite a user to a team
     */
    async inviteTeamMember(teamId: string, inviteData: { email: string; role: TeamMemberRole }): Promise<void> {
        const response = await apiClient.post(`/teams/${teamId}/invite`, inviteData);
        if (!response.success) {
            throw new Error(response.message || 'Failed to invite team member');
        }
    }

    /**
     * Accept a team invite
     */
    async acceptTeamInvite(token: string): Promise<void> {
        const response = await apiClient.post(`/teams/invites/${token}/accept`);
        if (!response.success) {
            throw new Error(response.message || 'Failed to accept team invite');
        }
    }

    /**
     * Remove a team member
     */
    async removeTeamMember(teamId: string, userId: string): Promise<void> {
        const response = await apiClient.delete(`/teams/${teamId}/members/${userId}`);
        if (!response.success) {
            throw new Error(response.message || 'Failed to remove team member');
        }
    }

    /**
     * Update a team member's role
     */
    async updateTeamMemberRole(teamId: string, userId: string, role: TeamMemberRole): Promise<void> {
        const response = await apiClient.put(`/teams/${teamId}/members/${userId}/role`, { role });

        // The API client already handles the ApiResponse wrapper, so response.data contains the actual response
        if (!response.success) {
            throw new Error(response.message || 'Failed to update team member role');
        }
    }

    /**
     * Update team key mode (default/byok)
     */
    async updateTeamKeyMode(teamId: string, keyMode: TeamKeyMode): Promise<UpdateTeamKeyModeResponse> {
        const payload: UpdateTeamKeyModeRequest = { keyMode };
        const response = await apiClient.put<UpdateTeamKeyModeResponse>(`/teams/${teamId}/key-mode`, payload);
        if (!response.success || !response.data) {
            throw new Error(response.message || 'Failed to update team key mode');
        }
        return response.data;
    }

    /**
     * Check if a slug is available
     */
    async checkSlugAvailability(slug: string): Promise<boolean> {
        const response = await apiClient.get<SlugAvailabilityResponse>(`/teams/check-slug/${slug}`);
        if (!response.data) {
            throw new Error('Failed to check slug availability');
        }
        return response.data.available;
    }

    /**
     * Generate a unique slug from team name
     */
    async generateSlug(name: string): Promise<string> {
        const response = await apiClient.post<GenerateSlugResponse>('/teams/generate-slug', { name });
        if (!response.data) {
            throw new Error('Failed to generate slug');
        }
        return response.data.slug;
    }

    /**
     * Leave a team (remove current user from team)
     */
    async leaveTeam(teamId: string): Promise<void> {
        const response = await apiClient.delete(`/teams/${teamId}/leave`);
        if (!response.data?.success) {
            throw new Error(response.data?.message || 'Failed to leave team');
        }
    }

    /**
     * Get user's primary team (first team they're a member of)
     */
    async getUserPrimaryTeam(): Promise<UserTeamInfo | null> {
        const { teams } = await this.getUserTeams();
        return teams.length > 0 ? teams[0] : null;
    }

    /**
     * Check if user has team access
     */
    async hasTeamAccess(): Promise<boolean> {
        try {
            const { teams } = await this.getUserTeams();
            return teams.length > 0;
        } catch {
            return false;
        }
    }

    /**
     * Get user's team role for a specific team
     */
    async getUserTeamRole(teamId: string): Promise<TeamMemberRole | null> {
        try {
            const { teams } = await this.getUserTeams();
            const teamInfo = teams.find(t => t.team.id === teamId);
            return teamInfo?.role || null;
        } catch {
            return null;
        }
    }

    /**
     * Check if user is team owner
     */
    async isTeamOwner(teamId: string): Promise<boolean> {
        try {
            const { teams } = await this.getUserTeams();
            const teamInfo = teams.find(t => t.team.id === teamId);
            return teamInfo?.isOwner || false;
        } catch {
            return false;
        }
    }
}

// Create and export singleton instance
export const teamService = new TeamService();
export default teamService;
